import { useEffect, useRef, useState } from "react";
import { getUserApi } from "../api";

type BootstrapState = "idle" | "polling" | "ready";

export function useUserObjectExists(userId: string | null, enabled: boolean) {
  const [readyUserId, setReadyUserId] = useState<string | null>(null);
  const timeoutLength = useRef(1000);

  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;

    const poll = async (): Promise<void> => {
      try {
        const user = await getUserApi(userId);

        if (cancelled) return;

        if (user) {
          setReadyUserId(userId);
          return;
        }

        timeoutId = window.setTimeout(() => {
          void poll();
        }, timeoutLength.current);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to poll user data.", err);
        }

        timeoutId = window.setTimeout(() => {
          void poll();
        }, timeoutLength.current);
      } finally {
        timeoutLength.current =
          timeoutLength.current >= 20000 ? 20000 : timeoutLength.current + 1000;
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [enabled, userId]);

  const userBootstrapState: BootstrapState =
    !enabled || !userId ? "idle" : readyUserId === userId ? "ready" : "polling";

  return { userBootstrapState };
}
